import {
  type ChannelSeriesEntry,
  findChannelSeries,
} from "./viewerWindowChannelSlice";

export function normalizeChannelLabel(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, " ");
}

export function stripChannelSignalPrefix(label: string): string {
  return label.replace(/^(eeg|emg|eog|ecg)\s+/i, "").trim();
}

export function channelLabelsMatch(
  leftLabel: string,
  rightLabel: string,
): boolean {
  const left = normalizeChannelLabel(leftLabel);
  const right = normalizeChannelLabel(rightLabel);
  if (!left || !right) {
    return false;
  }
  if (left === right) {
    return true;
  }
  if (left.includes(right) || right.includes(left)) {
    return true;
  }

  const strippedLeft = normalizeChannelLabel(stripChannelSignalPrefix(leftLabel));
  const strippedRight = normalizeChannelLabel(stripChannelSignalPrefix(rightLabel));
  return (
    strippedLeft === strippedRight ||
    strippedLeft.includes(strippedRight) ||
    strippedRight.includes(strippedLeft)
  );
}

export function findChannelSeriesForMontageLabel<T extends ChannelSeriesEntry>(
  channelSeries: T[],
  montageLabel: string,
): T | undefined {
  return (
    findChannelSeries(channelSeries, montageLabel) ??
    channelSeries.find((series) => channelLabelsMatch(series.name, montageLabel))
  ) as T | undefined;
}

export function resolveSelectionMeasuredChannels<T extends ChannelSeriesEntry>(
  channelSeries: T[],
  requestedLabels: string[],
  totalVisibleMontageCount: number,
): T[] {
  if (channelSeries.length === 0) {
    return [];
  }

  const scopedToFullMontage =
    requestedLabels.length === 0 ||
    (totalVisibleMontageCount > 0 &&
      requestedLabels.length >= totalVisibleMontageCount);

  if (scopedToFullMontage) {
    return [...channelSeries];
  }

  const resolved: T[] = [];
  const seen = new Set<string>();
  requestedLabels.forEach((label) => {
    const series = findChannelSeriesForMontageLabel(channelSeries, label);
    if (series && !seen.has(series.name)) {
      resolved.push(series);
      seen.add(series.name);
    }
  });
  return resolved;
}

export function isFullMontageChannelScope(
  requestedLabels: string[],
  totalVisibleMontageCount: number,
): boolean {
  return (
    requestedLabels.length === 0 ||
    (totalVisibleMontageCount > 0 &&
      requestedLabels.length >= totalVisibleMontageCount)
  );
}

export const SEGMENT_SELECTION_DRAG_PX = 4;
export const SEGMENT_SELECTION_VERTICAL_RECTANGLE_PX = 8;

export function shouldUseRectangularSegmentSelection(input: {
  shiftKey: boolean;
  startClientY: number;
  currentClientY: number;
  alreadyRectangular: boolean;
}): boolean {
  if (input.alreadyRectangular || input.shiftKey) {
    return true;
  }
  return (
    Math.abs(input.currentClientY - input.startClientY) >=
    SEGMENT_SELECTION_VERTICAL_RECTANGLE_PX
  );
}

export function intersectChannelLabelLists(
  primary: string[],
  filter: string[],
): string[] {
  if (filter.length === 0) {
    return [...primary];
  }
  const normalizedFilter = filter.map((label) => normalizeChannelLabel(label));
  return primary.filter((label) =>
    normalizedFilter.some((candidate) => channelLabelsMatch(label, candidate)),
  );
}
